import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const PATH = "/learn/franchise-location-website-guide";
const ARTICLE = getArticleBySlug(PATH);
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;
const TITLE = "Franchise Location Website Guide for Colorado Owners (2026)";
const DESC =
  "How Colorado franchise owners should handle a location website when corporate already has one — locator pages, GBP, schema conflicts, and ranking strategy.";

export const Route = createFileRoute("/learn/franchise-location-website-guide")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({ title: TITLE, description: DESC }),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Marketing Strategy"
      read="12 min read"
      title="Franchise location website guide: corporate site + your Colorado pages"
      intro="Buying a franchise means inheriting a brand, a playbook, and a website you don't control. The question every new Colorado franchisee asks: do I need my own website, or is the corporate site enough? Here's the real answer — with the SEO mechanics that decide whether your phone rings."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Franchise location website guide" },
      ]}
      path={PATH}
      faqs={[
        { q: "Do I need my own website if corporate has one?", a: "It depends on your franchise agreement. Most national franchises require all marketing run through the corporate domain. If allowed, a microsite or location landing page on your own domain gives you control over local SEO, reviews capture, and lead routing." },
        { q: "What's a 'locator page' and why does it matter?", a: "It's the page corporate gives your specific location on the brand site (e.g. brand.com/co/colorado-springs). This is what ranks for your local searches. Optimizing it — hours, photos, GBP link, reviews — is the single highest-leverage SEO task for a franchisee." },
        { q: "Won't my own site compete with corporate for the same keywords?", a: "It can, and corporate's domain authority usually wins. Smart franchisees don't fight that — they build a microsite focused on hyperlocal long-tail content (neighborhoods, local landmarks, Spanish-language pages) that corporate won't touch." },
        { q: "Who controls the Google Business Profile?", a: "Usually you, as the location operator — but check your franchise agreement. GBP is your most valuable local asset and you want owner-level access on your own Google account, not corporate's." },
      ]}
    >
      <h2>Step 1: read the franchise agreement on marketing</h2>
      <p>Before spending a dollar on a website, find these sections in your FDD (Franchise Disclosure Document):</p>
      <ul>
        <li><strong>Marketing fund / brand fund contributions</strong> — typically 1–4% of gross revenue.</li>
        <li><strong>Local marketing minimum</strong> — many require 1–2% additional local spend.</li>
        <li><strong>Approved channels and creative</strong> — some forbid any non-approved web property.</li>
        <li><strong>Domain restrictions</strong> — most prohibit you owning yourbrand-colorado.com.</li>
        <li><strong>GBP ownership</strong> — who claims and manages the listing.</li>
      </ul>

      <h2>Step 2: maximize the corporate location page</h2>
      <p>For 80% of Colorado franchisees, this is the highest-ROI marketing move available. The page already exists at <code>brand.com/co/your-city</code> and inherits corporate's domain authority. Most franchisees never touch it.</p>
      <ul>
        <li>Submit current hours, holiday hours, and a phone number that rings to <em>you</em>, not a national call center.</li>
        <li>Upload 15–25 real photos of your specific location — interior, exterior, team, signage. Stock photos kill conversion.</li>
        <li>Push for a location-specific bio (the owner's name and story).</li>
        <li>Make sure the page links to <em>your</em> GBP and your booking system.</li>
        <li>If corporate allows reviews on the page, capture and rotate them weekly.</li>
      </ul>

      <h2>Step 3: own your Google Business Profile</h2>
      <p>GBP is the single biggest driver of local franchise calls in Colorado. <Link to="/learn/google-business-profile">Full GBP guide here.</Link> Franchisee-specific rules:</p>
      <ul>
        <li>Claim the listing on <em>your</em> Google account, not the brand's corporate account.</li>
        <li>Use the corporate-approved business name verbatim — adding city or service to the name violates Google's guidelines.</li>
        <li>Category, services, hours, and Q&amp;A are yours to manage. Post weekly.</li>
        <li>Reviews go to the location, not the brand. Ask every customer. <Link to="/learn/get-more-reviews">How to ask.</Link></li>
      </ul>

      <h2>Step 4: should you build a microsite?</h2>
      <p>If your franchise agreement allows it, a microsite at <strong>yourname.com</strong> (often required to be visually distinct from corporate) can do things corporate's locator page can't:</p>
      <ul>
        <li>Rank for hyperlocal long-tail: "[service] in [Colorado neighborhood]" pages corporate will never write.</li>
        <li>Run local Google Ads with location-specific landing pages.</li>
        <li>Capture leads directly into your own CRM, not the corporate funnel.</li>
        <li>Build an email list that's yours if you ever sell the franchise.</li>
        <li>Run Spanish-language or seasonal Colorado-specific content.</li>
      </ul>
      <p>Microsite ballpark: $2,500–$6,000 one-time + $50/month hosting + $300–$1,250/month ongoing local SEO. Compare to: <Link to="/learn/web-design-cost-colorado">web design cost in Colorado</Link>.</p>

      <h2>Step 5: avoid the schema and canonical conflict</h2>
      <p>The most common technical mistake: your microsite and corporate's location page both claim to be the canonical source for your business. Google picks one and the wrong one usually wins.</p>
      <ul>
        <li>Use <code>LocalBusiness</code> schema on your microsite with <code>sameAs</code> pointing to the corporate location page and your GBP.</li>
        <li>Don't duplicate corporate's copy verbatim — write your own. Duplicate content suppresses both pages.</li>
        <li>Keep NAP (name, address, phone) <strong>identical</strong> across GBP, corporate page, microsite, Yelp, and Facebook.</li>
      </ul>

      <h2>Multi-location franchisees</h2>
      <p>Run 3+ Colorado locations under one franchise agreement? You need a different playbook — a single microsite with one well-built location page per market, internal links between them, and per-location GBPs. Most national platforms can't handle this well; a custom build does. See <Link to="/learn/custom-web-design-vs-diy-platforms">custom vs DIY</Link>.</p>

      <h2>Related guides</h2>
      <ul>
        <li><Link to="/learn/first-time-website-owner-guide">First-time website owner's guide</Link></li>
        <li><Link to="/learn/new-business-website-checklist">New business website checklist</Link></li>
        <li><Link to="/learn/buying-an-existing-business-website">Buying an existing business? Audit the website first</Link></li>
        <li><Link to="/learn/local-seo-guide">Local SEO guide</Link></li>
        <li><Link to="/learn/service-area-pages">Service area pages that rank</Link></li>
        <li><Link to="/learn/google-business-profile">Google Business Profile setup</Link></li>
      </ul>
      <h2>Work with us</h2>
      <ul>
        <li><Link to="/web-design">Web design</Link> — custom builds for Colorado businesses</li>
        <li><Link to="/seo">SEO</Link> and <Link to="/local-seo">local SEO</Link> — get found on Google</li>
        <li><Link to="/google-maps">Google Maps optimization</Link></li>
        <li><Link to="/pricing">Transparent pricing</Link></li>
      </ul>

    </ArticleLayout>
  );
}
